import { getUserSignedInState } from "@/Auth";
import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { formatPhoneNumber } from "@brandclub/common-ui";
import { Box, Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StoreFinish } from "./ConfettiContainer";

interface OrderConfirmationProps {
  orderNumber?: string;
  customerName?: string;
  shippingAddress?: string;
  estimatedDeliveryDate?: Date;
  savedAmount?: number;
  savedAmountReason?: string;
  productImageUrl?: string;
}

type UserLoginStatus = "notChecked" | "loggedIn" | "notLoggedIn";

export const OrderConfirmation: React.FC<OrderConfirmationProps> = () => {
  const [searchParams] = useSearchParams();
  const phoneNumber = useAppSelector((state) => state.userProfile?.phoneNumber);
  const orderId = searchParams.get("orderId");
  const [userLoginStatus, setUserLoginStatus] =
    useState<UserLoginStatus>("notChecked");

  // Navigation function to go to checkout summary
  const navigate = useNavigate();
  const handleTrackOrderClick = () => {
    navigate(`/checkoutSummary?orderId=${orderId}`);
  };

  const handleContinueShoppingClick = () => {
    navigate("/");
  };

  useEffect(() => {
    const checkUserInfo = async () => {
      try {
        const { userInfo, signedIn } = await getUserSignedInState();
        if (userInfo && signedIn) {
          setUserLoginStatus("loggedIn");
          return;
        }
      } catch (error) {
        console.error(error);
      }
      // this route requires login, so redirect to login page
      setUserLoginStatus("notLoggedIn");
      navigate("/signin", {
        replace: true,
        state: {
          redirectPath: {
            pathname: "/checkout/thankyou",
            search: searchParams.toString(),
          },
        },
      });
    };

    checkUserInfo();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (orderId === null && userLoginStatus === "loggedIn") {
      console.warn("no order id found in the URL; navigating to purchase");
      navigate("/dashboard/purchase");
    }
  }, [orderId, navigate, userLoginStatus]);

  const renderCallToActions = () => {
    return (
      <Box display="flex" flexDirection="column" gap={"16px"}>
        <SolidButton
          onClick={handleTrackOrderClick}
          disableMaxWidth
          sx={{ width: "100%" }}
        >
          Track my order
        </SolidButton>
        <OutlineButton
          onClick={handleContinueShoppingClick}
          disableMaxWidth
          sx={{ width: "100%" }}
        >
          Continue shopping
        </OutlineButton>
      </Box>
    );
  };
  // Desktop version
  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <StoreFinish
        title="Your order is on the way!"
        subtitle={
          <>
            We're placing your order with the retailer on your behalf, saving
            you time and effort.
            <br />
            <br />
            All the perks, none of the hassle.
            <br />
            <br />
            Order ID: #{orderId}
            <br />
            <br />
            Order updates will be sent to {formatPhoneNumber(phoneNumber)}
          </>
        }
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        {renderCallToActions()}
      </StoreFinish>
    </Container>
  );
};

export default OrderConfirmation;
